import type { CSSProperties, ReactNode } from 'react'
import { cn } from '../lib'
import { designTokens } from '../lib/design-tokens'
import { typographyStyle } from '../lib/catalog'

type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'info'
type BadgeSize = 'sm' | 'md'

type BadgeProps = {
  variant?: BadgeVariant
  size?: BadgeSize
  dismissible?: boolean
  onDismiss?: () => void
  icon?: ReactNode
  className?: string
  children: ReactNode
}

const sizeStyles: Record<BadgeSize, { paddingX: string; paddingY: string; gap: string }> = {
  sm: { paddingX: '0.5rem', paddingY: '0.125rem', gap: '0.25rem' },
  md: { paddingX: '0.625rem', paddingY: '0.25rem', gap: '0.375rem' },
}

const variantStyles: Record<
  BadgeVariant,
  { background: string; color: string; borderColor: string }
> = {
  default: {
    background: designTokens.colors.background.alt,
    color: designTokens.colors.text.primary,
    borderColor: designTokens.colors.border.default,
  },
  success: {
    background: designTokens.colors.background.successSoft,
    color: designTokens.colors.text.successStrong,
    borderColor: designTokens.colors.text.success,
  },
  warning: {
    background: designTokens.colors.background.actionSecondaryHover,
    color: designTokens.colors.text.strong,
    borderColor: designTokens.colors.border.default,
  },
  error: {
    background: designTokens.colors.background.actionSecondaryPressed,
    color: designTokens.colors.semantic.error,
    borderColor: designTokens.colors.semantic.error,
  },
  info: {
    background: designTokens.colors.background.page,
    color: designTokens.colors.text.secondary,
    borderColor: designTokens.colors.border.default,
  },
}

const dismissIcon = (
  <svg viewBox="0 0 20 20" aria-hidden="true" className="h-3 w-3">
    <path d="M5 5l10 10M15 5L5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
)

export function Badge({
  variant = 'default',
  size = 'sm',
  dismissible = false,
  onDismiss,
  icon,
  className,
  children,
}: BadgeProps) {
  const palette = variantStyles[variant]
  const spacing = sizeStyles[size]
  const typography = typographyStyle(designTokens.typography.badge.sm)
  const style: CSSProperties = {
    background: palette.background,
    color: palette.color,
    borderColor: palette.borderColor,
    padding: `${spacing.paddingY} ${spacing.paddingX}`,
    gap: spacing.gap,
    borderRadius: designTokens.borders.radius.pill ?? '999px',
    ...typography,
  }

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border text-xs font-semibold',
        className
      )}
      style={style}
    >
      {icon ? <span className="inline-flex items-center">{icon}</span> : null}
      <span>{children}</span>
      {dismissible ? (
        <button
          type="button"
          className="inline-flex items-center text-current/70 transition hover:text-current"
          aria-label="Dismiss badge"
          onClick={onDismiss}
        >
          {dismissIcon}
        </button>
      ) : null}
    </span>
  )
}
